import type { CSSInterpolation, CSSObject } from '@emotion/css/create-instance'
import { css, cx } from '@emotion/css'
import React, {
  createContext,
  forwardRef,
  type ReactNode,
  type Ref,
  useContext,
  useMemo,
} from 'react'

import type { ReactInputProps } from '../../types'
import {
  type ColorToken,
  CssVars,
  isLinearGradientColor,
  type LinearGradientColor,
  type ProductTheme,
} from '../themes'
import type { TypeStyleName } from '../themes/typePalette'
import { TextColorOverride } from './TextColorOverride'
import { getColorValue, useTheme } from './ThemeProvider'

type TypeElement =
  | 'div'
  | 'span'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
type FontFamily = 'nib' | 'capsule'
type BookCoverSpec = {
  size: 'S' | 'M' | 'L'
  font?: FontFamily
}
type DisplaySpec = {
  size: 'S' | 'M' | 'L'
  font?: FontFamily
}
type TextSpec = {
  size: 'S' | 'M' | 'L'
  isBold?: boolean
  useTabularNumbers?: boolean
}
type TypeSpec =
  | ({ type: 'bookCover' } & BookCoverSpec)
  | ({ type: 'display' } & DisplaySpec)
  | ({ type: 'text' } & TextSpec)

const TEXT_TYPES_MAP = {
  L: 'textL',
  M: 'textM',
  S: 'textS',
} as const
const TEXT_BOLD_TYPES_MAP = {
  L: 'textLBold',
  M: 'textMBold',
  S: 'textSBold',
} as const
function getTextType({ isBold, size }: TextSpec): TypeStyleName {
  return isBold ? TEXT_BOLD_TYPES_MAP[size] : TEXT_TYPES_MAP[size]
}
const DISPLAY_TYPES_MAP = {
  capsule: {
    L: 'displayCapsuleL',
    M: 'displayCapsuleM',
    S: 'displayCapsuleS',
  },
  nib: {
    L: 'displayNibL',
    M: 'displayNibM',
    S: 'displayNibS',
  },
} as const
const BOOK_COVER_TYPES_MAP = {
  capsule: {
    L: 'bookCoverCapsuleL',
    M: 'bookCoverCapsuleM',
    S: 'bookCoverCapsuleS',
  },
  nib: {
    L: 'bookCoverNibL',
    M: 'bookCoverNibM',
    S: 'bookCoverNibS',
  },
} as const

export const TypeContext = createContext<TypeSpec | null>(null)

/**
 * Returns a CSS styles object for the specified text typography.
 */
export function getStylesForTextType(spec: TextSpec): CSSObject {
  return getTypeStyles(getTextType(spec))
}

/**
 * Returns a set of CSS properties that produce the correct typography for the specified type.
 */
function getTypeStyles(
  type: TypeStyleName,
  useTabularNumbers?: boolean
): CSSObject {
  const typeVars = CssVars.type[type]
  return {
    color: CssVars.textColor,
    fontFamily: typeVars.stack,
    fontFeatureSettings: useTabularNumbers ? '"tnum"' : undefined,
    fontSize: typeVars.fontSize,
    fontStyle: typeVars.fontStyle || 'normal',
    // NOTE(jare): cast as any since we're using a CSS variable here and
    // fontWeight only accepts numbers
    fontWeight: typeVars.fontWeight as any,
    letterSpacing: typeVars.letterSpacing,
    lineHeight: typeVars.lineHeight,
    textDecoration: typeVars.textDecoration || 'unset',
  }
}

type _TypeProps = {
  'aria-label'?: string
  type: TypeStyleName
  children: ReactNode
  color?: ColorToken | LinearGradientColor | null
  useTabularNumbers?: boolean
  element: TypeElement
  maxLines?: number
  overrideRootStyles?: CSSInterpolation
}

function typeStyles(typeProps: _TypeProps, theme: ProductTheme) {
  const { color, maxLines, overrideRootStyles, type, useTabularNumbers } =
    typeProps

  let gradient

  if (isLinearGradientColor(color)) {
    gradient = `linear-gradient(${color.angle}deg, ${color.colorStops.map(
      colorStop =>
        `${getColorValue(colorStop.color, theme)} ${colorStop.stop * 100}%`
    )})`
  }

  return css([
    // NOTE(jare): Remove default styles from heading and paragraph elements
    { margin: 0 },
    getTypeStyles(type, useTabularNumbers),
    maxLines && {
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: maxLines,
      display: '-webkit-box',
      overflow: 'hidden',
    },
    gradient && {
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      backgroundImage: gradient,
    },
    overrideRootStyles,
  ])
}

export function _Type(props: _TypeProps) {
  const { 'aria-label': ariaLabel, children, color, element: Element } = props
  const theme = useTheme()

  const styles = typeStyles(props, theme)

  if (color && !isLinearGradientColor(color)) {
    return (
      <TextColorOverride color={color}>
        {({ rootClassName }) => (
          <Element className={cx(rootClassName, styles)}>{children}</Element>
        )}
      </TextColorOverride>
    )
  }

  return (
    <Element aria-label={ariaLabel} className={styles}>
      {children}
    </Element>
  )
}

type BookCoverProps = BookCoverSpec & {
  children: ReactNode
  color?: ColorToken | LinearGradientColor
  /**
   * The HTML tag that should be used to render this text. You can use this to
   * make the hierarchy or semantics of the text clear for accessibility (e.g.
   * to aid screen readers) or SEO purposes.
   */
  element?: TypeElement
  /**
   * If you want to clamp your text to a particular number of lines, specify the number here
   * as a positive integer. The text will be truncated after that many lines and an ellipsis
   * will be added.
   */
  maxLines?: number
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
}

// NOTE(eric.lovett): This is only exported so that the props will be picked up
// in our Storybook docs. Do not use this export directly.
export function BookCover({
  children,
  color,
  element = 'h2',
  font = 'capsule',
  maxLines,
  overrideRootStyles,
  size,
}: BookCoverProps) {
  const contextValue = useMemo(
    () => ({ size, type: 'bookCover' as const }),
    [size]
  )

  return (
    <_Type
      color={color}
      element={element}
      maxLines={maxLines}
      overrideRootStyles={overrideRootStyles}
      type={BOOK_COVER_TYPES_MAP[font][size]}
    >
      <TypeContext.Provider value={contextValue}>
        {children}
      </TypeContext.Provider>
    </_Type>
  )
}
BookCover.displayName = 'Type.BookCover'

type DisplayProps = DisplaySpec & {
  children: ReactNode
  color?: ColorToken | LinearGradientColor
  /**
   * The HTML tag that should be used to render this text. You can use this to
   * make the hierarchy or semantics of the text clear for accessibility (e.g.
   * to aid screen readers) or SEO purposes.
   */
  element?: TypeElement
  /**
   * If you want to clamp your text to a particular number of lines, specify the number here
   * as a positive integer. The text will be truncated after that many lines and an ellipsis
   * will be added.
   */
  maxLines?: number
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
}

/**
 * A component for applying typography styles, used for most heading text throughout our apps.
 */
// NOTE(eric.lovett): This is only exported so that the props will be picked up
// in our Storybook docs. Do not use this export directly.
export function Display({
  children,
  color,
  element = 'span',
  font = 'capsule',
  maxLines,
  overrideRootStyles,
  size,
}: DisplayProps) {
  const contextValue = useMemo(
    () => ({ size, type: 'display' as const }),
    [size]
  )

  return (
    <_Type
      color={color}
      element={element}
      maxLines={maxLines}
      overrideRootStyles={overrideRootStyles}
      type={DISPLAY_TYPES_MAP[font][size]}
    >
      <TypeContext.Provider value={contextValue}>
        {children}
      </TypeContext.Provider>
    </_Type>
  )
}
Display.displayName = 'Type.Display'

type TextProps = TextSpec & {
  children: ReactNode
  color?: ColorToken | LinearGradientColor | null
  /**
   * The HTML tag that should be used to render this text. You can use this to
   * make the hierarchy or semantics of the text clear for accessibility (e.g.
   * to aid screen readers) or SEO purposes.
   */
  element?: TypeElement
  /**
   * If you want to clamp your text to a particular number of lines, specify the number here
   * as a positive integer. The text will be truncated after that many lines and an ellipsis
   * will be added.
   */
  maxLines?: number
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
  /**
   * A human readable label for the all content in this component.
   */
  'aria-label'?: string
}

/**
 * A component for applying typography styles, used for most body text throughout our apps.
 */
// NOTE(eric.lovett): This is only exported so that the props will be picked up
// in our Storybook docs. Do not use this export directly.
export function Text({
  'aria-label': ariaLabel,
  children,
  color,
  element = 'span',
  isBold = false,
  maxLines,
  overrideRootStyles,
  size,
  useTabularNumbers,
}: TextProps) {
  const contextValue = useMemo(
    () => ({ isBold, size, type: 'text' as const, useTabularNumbers }),
    [size, isBold, useTabularNumbers]
  )
  return (
    <_Type
      aria-label={ariaLabel}
      color={color}
      element={element}
      maxLines={maxLines}
      overrideRootStyles={overrideRootStyles}
      type={getTextType({ isBold, size })}
      useTabularNumbers={useTabularNumbers}
    >
      <TypeContext.Provider value={contextValue}>
        {children}
      </TypeContext.Provider>
    </_Type>
  )
}
Text.displayName = 'Type.Text'

// TODO(eric.lovett): Determine if we can replace this with a TextInput.
const ExperimentalInput = forwardRef(function _ExperimentalInput(
  { typography, ...props }: ReactInputProps & { typography: TypeSpec },
  ref: Ref<HTMLInputElement>
) {
  let type

  if (typography.type === 'text') {
    type = getTextType(typography)
  } else if (typography.type === 'display') {
    type = DISPLAY_TYPES_MAP.capsule[typography.size]
  } else {
    type = BOOK_COVER_TYPES_MAP[typography.font ?? 'capsule'][typography.size]
  }

  return (
    <input
      ref={ref}
      // TODO(eric.lovett): Remove this class name once legacy input styles are removed
      // from web-app.
      className="remove-legacy"
      css={[
        {
          '&:placeholder': {
            color: CssVars.neutral.fg2,
          },
          backgroundColor: 'transparent',
          color: CssVars.textColor,
        },
        getTypeStyles(type),
      ]}
      {...props}
    />
  )
})
ExperimentalInput.displayName = 'Type.ExperimentalInput'

/**
 * A component for modifying the current typography style. This is useful if you want to have
 * one part of a block of text maintain the same sizing as the text around it while changing
 * the color or whether or not its bold.
 */
// NOTE(eric.lovett): This is only exported so that the props will be picked up
// in our Storybook docs. Do not use this export directly.
export function Extend({
  children,
  color,
  isBold,
  maxLines,
  overrideRootStyles,
}: {
  isBold?: boolean
  color?: ColorToken
  children: ReactNode
  maxLines?: number
  overrideRootStyles?: CSSInterpolation
}) {
  const context = useContext(TypeContext)
  if (!context) {
    throw new Error('<Type.Extend> can only be used inside of a Type component')
  }

  if (context.type === 'display') {
    return (
      <Display
        color={color}
        maxLines={maxLines}
        overrideRootStyles={overrideRootStyles}
        size={context.size}
      >
        {children}
      </Display>
    )
  }

  if (context.type === 'bookCover') {
    return (
      <BookCover
        color={color}
        maxLines={maxLines}
        overrideRootStyles={overrideRootStyles}
        size={context.size}
      >
        {children}
      </BookCover>
    )
  }

  return (
    <Text
      color={color}
      isBold={isBold || context.isBold}
      maxLines={maxLines}
      overrideRootStyles={overrideRootStyles}
      size={context.size}
    >
      {children}
    </Text>
  )
}
Extend.displayName = 'Type.Extend'

export const Type = {
  BookCover,
  Display,
  ExperimentalInput,
  Extend,
  Text,
}
